import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Sample project gallery data
  const galleryItems = [
    {
      id: 1,
      title: 'Commercial Building Demolition',
      category: 'Demolition',
      description: 'Complete demolition of a 1 story commercial building in Crescent City, Florida.',
      image: '/images/gallery/demo1.jpg',
    },
    {
      id: 2,
      title: 'Residential Land Clearing',
      category: 'Site Clearing',
      description: 'Land clearing and preparation for a new residential construction project.',
      image: '/images/gallery/clearing1.jpg',
    },
    {
      id: 3,
      title: 'Industrial Concrete Removal',
      category: 'Concrete Removal',
      description: 'Removal of concrete foundation from an industrial site.',
      image: '/images/gallery/concrete1.jpg',
    },
    {
      id: 4,
      title: 'Shopping Center Demolition',
      category: 'Demolition',
      description: 'Partial demolition of an outdated shopping center for renovation.',
      image: '/images/gallery/demo2.jpg',
    },
    {
      id: 5,
      title: 'Tree Removal for Development',
      category: 'Tree Removal',
      description: 'Large-scale tree removal for a new housing development.',
      image: '/images/gallery/tree1.jpg',
    },
    {
      id: 6,
      title: 'Office Building Interior Demolition',
      category: 'Interior Demolition',
      description: 'Interior demolition for office building renovation project.',
      image: '/images/gallery/interior1.jpg',
    }
  ];

  const openLightbox = useCallback((image) => {
    const index = galleryItems.findIndex(item => item.id === image.id);
    setSelectedIndex(index);
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  }, [galleryItems]);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  }, []);

  const goToNext = useCallback((e) => {
    e && e.stopPropagation();
    const nextIndex = (selectedIndex + 1) % galleryItems.length;
    setSelectedIndex(nextIndex);
    setSelectedImage(galleryItems[nextIndex]);
  }, [selectedIndex, galleryItems]);

  const goToPrevious = useCallback((e) => {
    e && e.stopPropagation();
    const prevIndex = (selectedIndex - 1 + galleryItems.length) % galleryItems.length;
    setSelectedIndex(prevIndex);
    setSelectedImage(galleryItems[prevIndex]);
  }, [selectedIndex, galleryItems]);

  // Carousel settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    adaptiveHeight: false,
    fade: true,
    arrows: true
  };

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      switch (e.key) {
        case 'ArrowRight':
          goToNext(e);
          break;
        case 'ArrowLeft':
          goToPrevious(e);
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectedIndex, goToNext, goToPrevious, closeLightbox]);

  return (
    <GallerySection className="section">
      <div className="container">
        <div className="section-title">
          <h2>Our Projects</h2>
          <p>View some of our recent demolition and site clearing work</p>
        </div>
        
        <CarouselContainer>
          <StyledSlider {...sliderSettings}>
            {galleryItems.map((item) => (
              <CarouselSlide key={item.id} onClick={() => openLightbox(item)}>
                <CarouselImage style={{ backgroundImage: `url(${item.image || 'https://via.placeholder.com/1200x600'})` }}>
                  <CarouselOverlay>
                    <CarouselCategory>{item.category}</CarouselCategory>
                    <CarouselTitle>{item.title}</CarouselTitle>
                    <CarouselDescription>{item.description}</CarouselDescription>
                  </CarouselOverlay>
                </CarouselImage>
              </CarouselSlide>
            ))}
          </StyledSlider>
        </CarouselContainer>
        
        <GalleryLink>
          <Link to="/gallery" className="btn">View All Projects</Link>
        </GalleryLink>
      </div>
      
      {selectedImage && (
        <Lightbox onClick={closeLightbox}>
          <LightboxNavButton 
            left 
            onClick={goToPrevious} 
            aria-label="Previous image"
            className="nav-button"
          >
            <div className="arrow-container">
              {isMobile ? (
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="3" fill="none" className="arrow-icon">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              ) : (
                <FaChevronLeft aria-hidden="true" className="arrow-icon" />
              )}
            </div>
          </LightboxNavButton>
          
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <LightboxClose onClick={closeLightbox}>
              <FaTimes />
              <span>Close</span>
            </LightboxClose>
            <LightboxImage src={selectedImage.image || 'https://via.placeholder.com/800x600'} alt={selectedImage.title} />
            <LightboxCaption>
              <h3>{selectedImage.title}</h3>
              <p>{selectedImage.description}</p>
              <LightboxCategory>{selectedImage.category}</LightboxCategory>
              <LightboxCounter>{selectedIndex + 1} of {galleryItems.length}</LightboxCounter>
            </LightboxCaption>
          </LightboxContent>
          
          <LightboxNavButton 
            right 
            onClick={goToNext} 
            aria-label="Next image"
            className="nav-button"
          >
            <div className="arrow-container">
              {isMobile ? (
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="3" fill="none" className="arrow-icon">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              ) : (
                <FaChevronRight aria-hidden="true" className="arrow-icon" />
              )}
            </div>
          </LightboxNavButton>
        </Lightbox>
      )}
    </GallerySection>
  );
};

const GallerySection = styled.section`
  background-color: var(--background-color);
  
  .section-title {
    p {
      max-width: 600px;
      margin: 0 auto;
      font-size: 1.8rem;
      color: white;
    }
  }
`;

const CarouselContainer = styled.div`
  margin: 5rem 0;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const StyledSlider = styled(Slider)`
  .slick-dots {
    bottom: 20px;
    
    li button:before {
      color: white;
      opacity: 0.7;
      font-size: 12px;
    }
    
    li.slick-active button:before {
      color: white;
      opacity: 1;
    }
  }
  
  .slick-prev, .slick-next {
    z-index: 10;
    width: 50px;
    height: 50px;
    
    &:before {
      font-size: 30px;
    }
  }
  
  .slick-prev {
    left: 20px;
  }
  
  .slick-next {
    right: 20px;
  }
`;

const CarouselSlide = styled.div`
  position: relative;
  height: 600px;
  cursor: pointer;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 992px) {
    height: 500px;
  }
  
  @media (max-width: 768px) {
    height: 400px;
  }
  
  @media (max-width: 576px) {
    height: 300px;
  }
`;

const CarouselImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.8s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const CarouselOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 5rem 3rem 3rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0));
`;

const CarouselCategory = styled.span`
  display: inline-block;
  background-color: var(--secondary-color);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 30px;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const CarouselTitle = styled.h3`
  color: white;
  font-size: 3.2rem;
  margin: 0 0 1rem;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const CarouselDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.8rem;
  max-width: 800px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const GalleryLink = styled.div`
  text-align: center;
  margin-top: 4rem;
  
  .btn {
    background-color: var(--primary-color);
    color: white;
    padding: 1.5rem 3rem;
    
    &:hover {
      background-color: var(--secondary-color);
    }
  }
`;

const Lightbox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  .nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 576px) {
    padding: 0;
    align-items: center;
  }
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 900px;
  width: 100%;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 576px) {
    margin: 0 1.5rem;
    max-height: calc(100vh - 10rem);
    display: flex;
    flex-direction: column;
  }
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: var(--secondary-color);
  color: white;
  border: none;
  width: auto;
  min-width: 4rem;
  height: 4rem;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 1.8rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  padding: 0 1.5rem;
  
  span {
    display: none;
    margin-left: 0.5rem;
    font-size: 1.4rem;
    font-weight: 600;
  }
  
  &:hover {
    background-color: var(--primary-color);
    width: auto;
    border-radius: 50px;
    
    span {
      display: inline;
    }
  }
`;

const LightboxImage = styled.img`
  width: 100%;
  display: block;
  max-height: 60vh;
  object-fit: cover;
  
  @media (max-width: 576px) {
    max-height: 50vh;
  }
`;

const LightboxCaption = styled.div`
  padding: 2.5rem;
  
  h3 {
    font-size: 2.4rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.6rem;
    color: var(--dark-gray);
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem;
    
    h3 {
      font-size: 2rem;
      margin-bottom: 0.8rem;
    }
    
    p {
      font-size: 1.4rem;
      margin-bottom: 1rem;
    }
  }
`;

const LightboxCategory = styled.span`
  display: inline-block;
  background-color: var(--light-gray);
  color: var(--primary-color);
  padding: 0.5rem 1rem;
  border-radius: 3px;
  font-size: 1.4rem;
  font-weight: 600;
`;

const LightboxNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.left ? 'left: 2rem;' : 'right: 2rem;'}
  background: rgba(0, 0, 0, 0.75);
  color: white;
  border: 2px solid white;
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  padding: 0;
  
  .arrow-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
  
  .arrow-icon {
    width: 50%;
    height: 50%;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.5));
  }
  
  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 992px) {
    width: 4.5rem;
    height: 4.5rem;
  }
  
  @media (max-width: 768px) {
    width: 4.5rem;
    height: 4.5rem;
    ${props => props.left ? 'left: 1rem;' : 'right: 1rem;'}
    border-width: 2px;
  }
  
  @media (max-width: 576px) {
    width: 5.5rem;
    height: 5.5rem;
    ${props => props.left ? 'left: 1.2rem;' : 'right: 1.2rem;'}
    background: rgba(0, 0, 0, 0.85);
    border: 3px solid white;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.5);
    
    .arrow-icon {
      width: 60%;
      height: 60%;
      filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.8));
    }
    
    &:hover, &:active {
      transform: translateY(-50%) scale(1.1);
      background-color: var(--primary-color);
    }
  }
`;

const LightboxCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 2.5rem;
  color: var(--dark-gray);
  font-size: 1.2rem;
  font-weight: 500;
`;

export default Gallery;